import type { Product } from "@/interfaces/product";
import type {
  CustomLocation,
  GiftContext,
  GiftResolve,
  RecipientDetails,
  RedeemGiftResult,
  RedemptionFeedResponse,
} from "@/interfaces/redemption";
import { apiClient } from "@/lib/apiClient";

/**
 * Recipient endpoints — the public gift-redemption API (Hono `redemptionRoutes`).
 *
 * Auth is the gift-link token itself (a public route), NOT Clerk/RBAC — the opaque
 * `:token` in the path resolves to a `contacts` / `campaigns` row via
 * `shortUrls.suffix`. The token being unguessable is the credential.
 */

/**
 * Resolve the tokenised gift link → recipient + campaign context.
 *
 * BACKEND: the returned `context` must include `shipping` (see GiftShipping) —
 * derived from the campaign's plan tier + the recipient-list country column —
 * so the frontend knows the destination country and whether it's locked. Absent
 * ⇒ the frontend defaults to US, unlocked.
 */
export async function resolveGiftToken(token: string): Promise<GiftResolve> {
  const { data } = await apiClient.get<
    | { context: GiftContext }
    | { notYetActive: { activationDate: string | null } }
    | { limitReached: true }
  >(`/redemption/${token}`);
  // A self-send link before it goes live resolves to the "not yet active" marker (200); the page
  // renders the go-live date (or "launches soon" when there's no date) instead of the gift experience.
  if ("notYetActive" in data) {
    return { notYetActive: true, activationDate: data.notYetActive.activationDate };
  }
  // A universal link whose redemption limit is used up resolves to "limit reached" (200).
  if ("limitReached" in data) {
    return { limitReached: true };
  }
  return data.context;
}

/**
 * The campaign's feed as RAW nested collections + per-country edits + added-product data.
 * The client composes → filters by country → applies that country's edits, exactly like the
 * campaign builder (shared resolveCountryFeed). Resolved from the token, campaign-scoped.
 */
export async function fetchRedemptionProducts(token: string): Promise<RedemptionFeedResponse> {
  const { data } = await apiClient.get<RedemptionFeedResponse>(`/redemption/${token}/products`);
  return data;
}

/**
 * Full product detail for the recipient slide-in (variants / story / impact). The feed
 * payload is a light subset; this fetches the rest. Public + token-scoped (the recipient
 * is unauthenticated), so it hits the redemption route, not the authed collections one.
 */
export async function fetchRedemptionProductDetail(
  token: string,
  productId: string,
): Promise<Product> {
  const { data } = await apiClient.get<{ product: Product }>(
    `/redemption/${token}/products/${productId}`,
  );
  return data.product;
}

export interface SubmitRedemptionPayload {
  token: string;
  variantId: string;
  recipient: RecipientDetails;
  /** Well Told custom-map location; the API requires it when the product asks for one. */
  customLocation?: CustomLocation;
}

/**
 * Write the recipient's chosen variant + shipping details onto the contact and
 * advance it to `confirmed` (v1 `confirm-gift` parity). The Shopify order is created
 * asynchronously downstream, so the response is just the affected contact id.
 */
export async function submitRedemption(
  payload: SubmitRedemptionPayload,
): Promise<RedeemGiftResult> {
  const { token, ...body } = payload;
  const { data } = await apiClient.post<RedeemGiftResult>(`/redemption/${token}/redeem`, body);
  return data;
}

/**
 * Save the recipient's thank-you note to the sender (contacts.thankNote).
 *
 * `contactId` is the placeholder the recipient just claimed (from the redeem response). It's
 * REQUIRED for a Universal Link — the shared /u/ token has no bound contact, so without it the
 * note has no one to attribute to (the API 404s). Harmless for gfg/manual, where the bound token
 * already identifies the contact.
 */
export async function sendThankYou(token: string, note: string, contactId?: number): Promise<void> {
  await apiClient.post(`/redemption/${token}/message`, {
    note,
    ...(contactId != null ? { contactId } : {}),
  });
}
